import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import { Breadcrumbs, Stack, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useQueries } from '@tanstack/react-query'
import { groupBy, map, pipe, pluck, prop, reduce } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import { DataGridPro, intuitiveFromNow } from '../../consts/Theme'
import { AGREEMENTS_QUERY_KEY, AgreementModel } from '../../features/Agreements'
import { AgreementsAPI } from '../../features/Agreements/api'
import { ControlGroup, useAllControlGroups } from '../../features/ControlGroups'
import { ROUTE } from '../../navigation/types'

export const ControlGroups = () => {
  const controlPlots = useAllControlGroups()
  const navigate = useNavigate()
  const { t } = useTranslation('impact/pages/ControlGroups')

  const controlGroups: Record<string, ControlGroup[]> = groupBy(
    prop('agreementId'),
    controlPlots.data || []
  )

  const agreementRows: {
    id: string
    agreementId: string
    plots: number
    isCommitted: boolean
    validCount: number
    validRatio: number
    createdAt: number
    userName?: string
    committedAt?: number
  }[] = Object.entries(controlGroups).map(([agreementId, groups]) => {
    const createdAt = pipe<
      Array<ControlGroup>,
      Array<{ createdDate: string }>,
      Array<string>,
      Array<number>,
      number
    >(
      (groups: ControlGroup[]) => {
        const withDates: Array<{ createdDate: string }> = []
        groups.forEach(cg => {
          cg.createdDate && withDates.push(cg as { createdDate: string })
        })
        return withDates
      },
      pluck('createdDate'),
      map((createdDate: string) => new Date(createdDate).valueOf()),
      reduce((acc, item: number) => Math.max(acc, item), 0)
    )(groups)

    const committedAt = pipe<
      Array<ControlGroup>,
      Array<{ committedAt: string }>,
      Array<string>,
      Array<number>,
      number
    >(
      (groups: ControlGroup[]) => {
        const withDates: Array<{ committedAt: string }> = []
        groups.forEach(cg => {
          cg.committedAt && withDates.push(cg as { committedAt: string })
        })
        return withDates
      },
      pluck('committedAt'),
      map((committedAt: string) => new Date(committedAt).valueOf()),
      reduce((acc, item: number) => Math.max(acc, item), 0)
    )(groups)

    const validCount = groups.filter(
      ({ status, modelUnpaired }) => modelUnpaired || status === 'valid'
    ).length

    return {
      agreementId,
      committedAt,
      createdAt,
      id: agreementId,
      isCommitted: groups.every(controlPlot => controlPlot.isCommitted),
      validCount,
      validRatio: groups.length ? validCount / groups.length : 0,
      plots: groups.length,
    }
  })

  const agreements = useQueries({
    queries: agreementRows.map(({ agreementId }) => ({
      queryKey: [AGREEMENTS_QUERY_KEY.AGREEMENTS, agreementId],
      queryFn: () =>
        AgreementsAPI.getAgreement({ agreementId }).then(response => new AgreementModel(response)),
    })),
  })

  agreementRows.forEach((row, i) => {
    row.userName = agreements[i]?.data?.userModel?.displayNameFull
  })

  const loadingAgreements = agreements.some(a => a.isLoading)

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'createdAt',
        minWidth: 200,
        headerName: t('createdAt'),
        renderCell: ({ value }: GridRenderCellParams) => !!value && intuitiveFromNow(value),
      },
      {
        field: 'userName',
        headerName: t('user'),
        flex: 1,
        renderCell: ({ value }: GridRenderCellParams) => value,
        sortable: true,
        disableColumnMenu: true,
      },
      {
        field: 'plots',
        headerName: t('plots'),
        align: 'center',
        disableColumnMenu: true,
      },
      {
        field: 'validRatio',
        headerName: t('isValid'),
        minWidth: 120,
        renderCell: ({ row }) =>
          row.validCount === row.plots ? (
            <CheckIcon color="success" />
          ) : (
            <Typography>
              {row.validCount}/{row.plots}
            </Typography>
          ),
        align: 'center',
        disableColumnMenu: true,
      },
      {
        field: 'committedAt',
        headerName: t('committedAt'),
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            {params.row.isCommitted ? <CheckCircleOutline color="success" /> : null}
            {params.value ? <Typography>{intuitiveFromNow(params.value)}</Typography> : null}
          </Stack>
        ),
        align: 'center',
        disableColumnMenu: true,
      },
    ],
    []
  )

  return (
    <Stack>
      <Breadcrumbs sx={{ p: 2 }}>
        <Typography>{t('controlGroups')}</Typography>
      </Breadcrumbs>
      <DataGridPro
        loading={controlPlots.isLoading || loadingAgreements}
        sx={{ px: 2 }}
        pageSize={200}
        rowsPerPageOptions={[200]}
        pagination
        autoHeight
        rows={agreementRows}
        onRowClick={({ row: { agreementId } }) =>
          navigate(generatePath(ROUTE.CONTROL_GROUPS_DETAIL, { agreementId }))
        }
        columns={columns}
        initialState={{ sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] } }}
      />
    </Stack>
  )
}
